class SealHomeTestimonials extends HTMLElement {
  connectedCallback() {
    this.marqueeRows = Array.from(this.querySelectorAll('[data-seal-testimonials-row]')).map((row) => ({
      row,
      cards: Array.from(row.querySelectorAll('[data-seal-testimonial-card]')),
      direction: row.dataset.direction === 'right' ? 'right' : 'left',
      positions: [],
      widths: [],
      trackLength: 0,
      cycleLength: 0,
      rowWidth: 0,
      offset: 0,
      activeIndexes: new Set(),
      hasPositioned: false,
      paused: false,
      dragging: false,
      pointerId: null,
      dragStartX: 0,
      dragStartOffset: 0,
    }));

    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.lastFrameTime = null;
    this.rafId = null;
    this.resizeFrame = null;

    const { signal } = this.abortController;

    for (const state of this.marqueeRows) {
      state.row.addEventListener('pointerdown', (event) => this.startPointerInteraction(state, event), { signal });
      state.row.addEventListener('pointermove', (event) => this.movePointerInteraction(state, event), { signal });
      state.row.addEventListener('pointerup', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('pointercancel', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('lostpointercapture', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('focusin', () => {
        this.pauseRow(state);
      }, { signal });
      state.row.addEventListener('focusout', () => {
        this.resumeRow(state);
      }, { signal });
    }

    window.addEventListener('pointerup', () => this.resumeAllRows(), { signal });
    window.addEventListener('pointercancel', () => this.resumeAllRows(), { signal });
    window.addEventListener('blur', () => this.resumeAllRows(), { signal });

    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this);
    for (const state of this.marqueeRows) {
      this.resizeObserver.observe(state.row);
    }

    this.mediaQuery.addEventListener('change', () => this.reset(), { signal });
    document.addEventListener('visibilitychange', () => {
      this.lastFrameTime = null;
    }, { signal });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.scheduleMeasure());
    }

    this.reset();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
    }

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
    }
  }

  reset() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.lastFrameTime = null;
    this.measure();

    if (this.mediaQuery.matches) {
      for (const state of this.marqueeRows) {
        state.offset = 0;
        this.showAllCards(state);
      }
      return;
    }

    this.rafId = window.requestAnimationFrame((time) => this.animate(time));
  }

  scheduleMeasure() {
    if (this.resizeFrame) return;

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.measure();
    });
  }

  measure() {
    const duration = parseFloat(getComputedStyle(this).getPropertyValue('--seal-home-testimonials-duration'));
    this.duration = Number.isFinite(duration) && duration > 0 ? duration * 1000 : 75000;

    for (const state of this.marqueeRows) {
      if (state.cards.length === 0) continue;

      state.row.style.removeProperty('--seal-home-testimonials-card-height');
      state.row.style.removeProperty('--seal-home-testimonials-row-height');

      const rowStyle = getComputedStyle(state.row);
      const gap = parseFloat(rowStyle.getPropertyValue('--seal-home-testimonials-gap')) || 0;
      const groupCount = Math.max(state.row.querySelectorAll('.seal-home-testimonials__group').length, 1);
      const cycleCardCount = Math.max(Math.round(state.cards.length / groupCount), 1);
      const positions = [];
      const widths = [];
      let cursor = 0;
      let maxHeight = 0;

      for (const card of state.cards) {
        const rect = card.getBoundingClientRect();
        const width = rect.width || card.offsetWidth || 1;
        const height = rect.height || card.offsetHeight || 1;

        positions.push(cursor);
        widths.push(width);
        cursor += width + gap;
        maxHeight = Math.max(maxHeight, height);
      }

      state.positions = positions;
      state.widths = widths;
      state.trackLength = Math.max(cursor, 1);
      state.cycleLength = Math.max(positions[cycleCardCount] || state.trackLength / groupCount || state.trackLength, 1);
      state.rowWidth = state.row.getBoundingClientRect().width || window.innerWidth || 1;
      state.offset %= state.trackLength;
      state.activeIndexes = new Set();
      state.hasPositioned = false;

      state.row.style.setProperty('--seal-home-testimonials-card-height', `${Math.ceil(maxHeight)}px`);
      state.row.style.setProperty('--seal-home-testimonials-row-height', `${Math.ceil(maxHeight + 10)}px`);
      if (this.mediaQuery.matches) {
        this.showAllCards(state);
      } else {
        this.positionCards(state);
      }
    }
  }

  animate(time) {
    if (document.hidden) {
      this.lastFrameTime = null;
      this.rafId = window.requestAnimationFrame((nextTime) => this.animate(nextTime));
      return;
    }

    if (this.lastFrameTime === null) {
      this.lastFrameTime = time;
    }

    const delta = Math.min(time - this.lastFrameTime, 64);
    this.lastFrameTime = time;

    for (const state of this.marqueeRows) {
      if (state.paused || state.trackLength <= 1) continue;

      const movement = (state.cycleLength / this.duration) * delta;
      state.offset = (state.offset + movement) % state.trackLength;

      this.positionCards(state);
    }

    this.rafId = window.requestAnimationFrame((nextTime) => this.animate(nextTime));
  }

  pauseRow(state) {
    state.paused = true;
  }

  resumeRow(state) {
    state.paused = false;
  }

  resumeAllRows() {
    for (const state of this.marqueeRows) {
      this.resumeRow(state);
    }
  }

  startPointerInteraction(state, event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    state.dragging = true;
    state.pointerId = event.pointerId;
    state.dragStartX = event.clientX;
    state.dragStartOffset = state.offset;
    state.row.classList.add('is-dragging');
    this.pauseRow(state);

    if (state.row.setPointerCapture) {
      state.row.setPointerCapture(event.pointerId);
    }
  }

  movePointerInteraction(state, event) {
    if (!state.dragging || state.pointerId !== event.pointerId || state.trackLength <= 1) return;

    const dragDistance = event.clientX - state.dragStartX;
    const dragOffset = state.direction === 'right' ? dragDistance : -dragDistance;

    state.offset = this.normalizeOffset(state.dragStartOffset + dragOffset, state.trackLength);
    this.positionCards(state);
  }

  endPointerInteraction(state, event) {
    if (state.pointerId !== null && state.pointerId !== event.pointerId) return;

    if (state.row.releasePointerCapture && state.pointerId !== null) {
      try {
        state.row.releasePointerCapture(state.pointerId);
      } catch (error) {
        // Safari may already release capture before lostpointercapture fires.
      }
    }

    state.dragging = false;
    state.pointerId = null;
    state.row.classList.remove('is-dragging');
    this.resumeRow(state);
  }

  normalizeOffset(offset, trackLength) {
    return ((offset % trackLength) + trackLength) % trackLength;
  }

  positionCards(state) {
    const buffer = 120;
    const rowWidth = state.rowWidth || window.innerWidth || 1;
    const trackLength = state.trackLength || 1;
    const nextActiveIndexes = new Set();

    state.cards.forEach((card, index) => {
      const width = state.widths[index] || card.offsetWidth || 1;
      let x = state.positions[index] || 0;

      if (state.direction === 'right') {
        x += state.offset;
        while (x > rowWidth + buffer) {
          x -= trackLength;
        }
      } else {
        x -= state.offset;
        while (x < -width - buffer) {
          x += trackLength;
        }
      }

      const isActive = x < rowWidth + buffer && x + width > -buffer;

      if (isActive || state.activeIndexes.has(index) || !state.hasPositioned) {
        card.style.setProperty('--seal-home-testimonials-card-x', `${x}px`);
        card.classList.toggle('is-active', isActive);
        card.classList.toggle('is-offscreen', !isActive);
      }

      if (isActive) {
        nextActiveIndexes.add(index);
      }
    });

    state.activeIndexes = nextActiveIndexes;
    state.hasPositioned = true;
  }

  showAllCards(state) {
    state.activeIndexes = new Set();
    state.hasPositioned = false;

    for (const card of state.cards) {
      card.classList.remove('is-active', 'is-offscreen');
      card.style.removeProperty('--seal-home-testimonials-card-x');
    }
  }
}

if (!customElements.get('seal-home-testimonials')) {
  customElements.define('seal-home-testimonials', SealHomeTestimonials);
}
